import { useEffect, useRef, useState } from 'react';
import {
  AUFGABEN_STATUS,
  PRIORITAETEN,
  PRIORITAET_NAMEN,
  STATUS_NAMEN,
  istUeberfaellig,
  type Aufgabe,
  type AufgabeDetail,
  type AufgabenLive,
  type Kommentar,
  type MindmapAuswahl,
  type PersonKurz,
} from '@beholder/shared/module/aufgaben';
import { Anlagen } from '../../core/Anlagen';
import { api, ApiFehler } from '../../core/api';
import type { ModulSeiteProps } from '../../core/modul';
import { Symbol } from '../../core/symbole';
import { Auswahl, Dialog, Eingabe, Feld, Knopf, Meldung, Textfeld, datumZeit, useAktion, useDaten } from '../../core/ui';
import { faelligText, pfad, ueberfaelligText } from './daten';
import { Personen } from './Zeilen';

interface Props extends ModulSeiteProps {
  a: Aufgabe;
  mitglieder: PersonKurz[];
  mindmap: MindmapAuswahl;
  schliessen(): void;
  geaendert(a: Aufgabe): void;
  geloescht(id: string): void;
}

export function Detail({ a, projekt, ich, darfSchreiben, mitglieder, mindmap, schliessen, geaendert, geloescht }: Props) {
  const aktion = useAktion();
  const url = (rest = '') => pfad(projekt.id, `/${a.id}${rest}`);
  const [abgebenOffen, setAbgebenOffen] = useState(false);
  const [anlagenStand, setAnlagenStand] = useState(0);
  useEffect(() => {
    const h = (e: Event) => (e as CustomEvent<string>).detail === a.id && setAnlagenStand((n) => n + 1);
    window.addEventListener('auf:anlagen', h);
    return () => window.removeEventListener('auf:anlagen', h);
  }, [a.id]);

  async function setzen(e: Record<string, unknown>) {
    const neu = await aktion.ausfuehren(() => api.patch<Aufgabe>(url(), e));
    if (neu) geaendert(neu);
    return neu;
  }
  async function senden(rest: string, body: unknown = {}) {
    const neu = await aktion.ausfuehren(() => api.post<Aufgabe>(url(rest), body));
    if (neu) geaendert(neu);
    return neu;
  }

  const meins = a.zugewiesen.some((p) => p.id === ich.id);
  const ueber = istUeberfaellig(a);
  const binMitglied = mitglieder.some((m) => m.id === ich.id);
  const freie = mitglieder.filter((m) => !a.zugewiesen.some((p) => p.id === m.id));

  return (
    <article className={`karte auf-detail${ueber ? ' auf-ueberfaellig' : ''}`} aria-label={`Aufgabe ${a.titel}`}>
      <div className="auf-detail-kopf">
        <Knopf art="leise" klein onClick={schliessen} aria-label="Zurück zur Liste" className="auf-zurueck">
          {Symbol.pfeilLinks} Liste
        </Knopf>
        <span className="abstand" />
        {darfSchreiben && (
          <Knopf
            art="leise"
            klein
            aria-label="Aufgabe in den Papierkorb legen"
            title="In den Papierkorb (180 Tage zurückholbar)"
            onClick={async () => {
              if (!confirm(`Aufgabe „${a.titel}“ mit Checkliste, Kommentaren und Anlagen in den Papierkorb legen? Dort kann sie 180 Tage lang zurückgeholt werden.`)) return;
              const ok = await aktion.ausfuehren(() => api.delete(url()));
              if (ok) geloescht(a.id);
            }}
          >
            {Symbol.muell}
          </Knopf>
        )}
      </div>

      {a.vorschlag && (
        <div className="auf-vorschlag-hinweis">
          <div>
            <strong>Vorschlag</strong> {a.herkunft && <span>– {a.herkunft}</span>}
            <div className="leise klein">Erst nach dem Übernehmen zählt er als richtige Aufgabe.</div>
          </div>
          {darfSchreiben && (
            <div className="reihe">
              <Knopf art="haupt" klein onClick={() => void senden('/annehmen')} disabled={aktion.laeuft}>
                Übernehmen
              </Knopf>
              <Knopf
                klein
                disabled={aktion.laeuft}
                onClick={async () => {
                  if (!confirm(`Vorschlag „${a.titel}“ verwerfen? Er liegt danach 180 Tage im Papierkorb.`)) return;
                  const ok = await aktion.ausfuehren(() => api.post(url('/verwerfen')));
                  if (ok) geloescht(a.id);
                }}
              >
                Verwerfen
              </Knopf>
            </div>
          )}
        </div>
      )}

      <TextBearbeiten
        wert={a.titel}
        version={a.version}
        darfSchreiben={darfSchreiben}
        feld="titel"
        url={url()}
        geaendert={geaendert}
        bezeichnung="Titel"
      />

      <Meldung>{aktion.fehler}</Meldung>

      <div className="auf-felder">
        <Feld titel="Status">
          {darfSchreiben ? (
            <div className="auf-status-wahl" role="radiogroup" aria-label="Status">
              {AUFGABEN_STATUS.map((s) => (
                <button
                  key={s}
                  type="button"
                  role="radio"
                  aria-checked={a.status === s}
                  className={`auf-status-knopf auf-status-${s}${a.status === s ? ' aktiv' : ''}`}
                  onClick={() => a.status !== s && void setzen({ status: s })}
                  disabled={aktion.laeuft}
                >
                  {STATUS_NAMEN[s]}
                </button>
              ))}
            </div>
          ) : (
            <span className={`auf-marke auf-status-${a.status}`}>{STATUS_NAMEN[a.status]}</span>
          )}
        </Feld>

        <Feld titel="Priorität">
          {darfSchreiben ? (
            <Auswahl value={a.prioritaet} onChange={(e) => void setzen({ prioritaet: e.target.value })}>
              {PRIORITAETEN.map((p) => (
                <option key={p} value={p}>
                  {PRIORITAET_NAMEN[p]}
                </option>
              ))}
            </Auswahl>
          ) : (
            <span>{PRIORITAET_NAMEN[a.prioritaet]}</span>
          )}
        </Feld>

        <Feld titel="Fällig" hinweis={ueber ? <span className="auf-text-ueber">⚠ {ueberfaelligText(a)}</span> : a.faellig ? faelligText(a.faellig) : undefined}>
          {darfSchreiben ? (
            <DatumFeld wert={a.faellig} speichern={(d) => void setzen({ faellig: d })} />
          ) : (
            <span className={ueber ? 'auf-text-ueber' : ''}>{a.faellig ? faelligText(a.faellig) : 'ohne Datum'}</span>
          )}
        </Feld>

        {mindmap.verfuegbar && (
          <Feld titel="Mindmap-Punkt">
            {darfSchreiben ? (
              <Auswahl value={a.mindmapKnotenId ?? ''} onChange={(e) => void setzen({ mindmapKnotenId: e.target.value || null })}>
                <option value="">– keiner –</option>
                {a.mindmapKnotenId && !mindmap.knoten.some((k) => k.id === a.mindmapKnotenId) && (
                  <option value={a.mindmapKnotenId}>(Punkt gibt es nicht mehr)</option>
                )}
                {mindmap.knoten.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.pfad}
                  </option>
                ))}
              </Auswahl>
            ) : (
              <span>{mindmap.knoten.find((k) => k.id === a.mindmapKnotenId)?.pfad ?? '–'}</span>
            )}
          </Feld>
        )}
      </div>

      <section className="auf-abschnitt">
        <h3>Zuständig</h3>
        <div className="auf-zustaendig">
          {a.zugewiesen.length === 0 && <span className="leise">Noch niemand.</span>}
          {a.zugewiesen.map((p) => (
            <span key={p.id} className={`auf-chip${p.id === ich.id ? ' auf-ich' : ''}`}>
              <Personen personen={[p]} ichId={ich.id} />
              {p.name}
              {darfSchreiben && (
                <button
                  type="button"
                  className="auf-chip-weg"
                  aria-label={`${p.name} entfernen`}
                  onClick={() => void setzen({ zugewiesenAn: a.zugewiesen.filter((x) => x.id !== p.id).map((x) => x.id) })}
                >
                  ✕
                </button>
              )}
            </span>
          ))}
        </div>
        {darfSchreiben && (
          <div className="reihe">
            {freie.length > 0 && (
              <Auswahl
                value=""
                aria-label="Person hinzufügen"
                className="auf-person-wahl"
                onChange={(e) => e.target.value && void setzen({ zugewiesenAn: [...a.zugewiesen.map((x) => x.id), e.target.value] })}
              >
                <option value="">+ Person hinzufügen …</option>
                {freie.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </Auswahl>
            )}
            {!meins && binMitglied && (
              <Knopf klein onClick={() => void senden('/uebernehmen')} disabled={aktion.laeuft}>
                Übernehmen
              </Knopf>
            )}
            {meins && (
              <Knopf klein onClick={() => setAbgebenOffen(true)} disabled={aktion.laeuft}>
                Abgeben …
              </Knopf>
            )}
          </div>
        )}
      </section>

      <section className="auf-abschnitt">
        <h3>Beschreibung</h3>
        <TextBearbeiten
          wert={a.beschreibung}
          version={a.version}
          darfSchreiben={darfSchreiben}
          feld="beschreibung"
          url={url()}
          geaendert={geaendert}
          bezeichnung="Beschreibung"
          mehrzeilig
        />
      </section>

      <Checkliste a={a} url={url} darfSchreiben={darfSchreiben} geaendert={geaendert} />

      <section className="auf-abschnitt">
        <h3>Anlagen</h3>
        {/* Bei Live-Hinweis vom Kern neu aufbauen, damit Anlagen anderer sofort erscheinen */}
        <Anlagen key={`${a.id}-${anlagenStand}`} projektId={projekt.id} bereich="aufgaben" bezug={a.id} darfSchreiben={darfSchreiben} />
      </section>

      <Kommentare a={a} url={url} ichId={ich.id} darfSchreiben={darfSchreiben} darfFremde={projekt.verwalten} />

      <p className="leise klein auf-meta">
        Angelegt {a.erstelltVon ? `von ${a.erstelltVon.name} ` : ''}am {datumZeit(a.erstelltAm)}
        {a.herkunft && !a.vorschlag ? ` · ${a.herkunft}` : ''}
        {a.erledigtAm ? ` · erledigt am ${datumZeit(a.erledigtAm)}` : ''} · zuletzt geändert {datumZeit(a.geaendertAm)}
      </p>

      <AbgebenDialog
        offen={abgebenOffen}
        schliessen={() => setAbgebenOffen(false)}
        kandidaten={mitglieder.filter((m) => m.id !== ich.id)}
        abgeben={async (an) => {
          const neu = await senden('/abgeben', { an });
          if (neu) setAbgebenOffen(false);
        }}
      />
    </article>
  );
}

/** Text bearbeiten ohne stilles Überschreiben: speichert beim Verlassen des Feldes mit der Version vom Bearbeitungsbeginn. */
function TextBearbeiten({
  wert,
  version,
  darfSchreiben,
  feld,
  url,
  geaendert,
  bezeichnung,
  mehrzeilig,
}: {
  wert: string;
  version: number;
  darfSchreiben: boolean;
  feld: 'titel' | 'beschreibung';
  url: string;
  geaendert(a: Aufgabe): void;
  bezeichnung: string;
  mehrzeilig?: boolean;
}) {
  const [entwurf, setEntwurf] = useState(wert);
  const [aktiv, setAktiv] = useState(false);
  const [konflikt, setKonflikt] = useState('');
  const [fehler, setFehler] = useState('');
  const start = useRef({ wert, version });
  // Während des Speicherns den Entwurf nicht durch Live-Stände ersetzen (sonst ginge er bei einem Konflikt verloren)
  const speichert = useRef(false);

  useEffect(() => {
    if (!aktiv && !konflikt && !speichert.current) {
      setEntwurf(wert);
      start.current = { wert, version };
    }
  }, [wert, version, aktiv, konflikt]);

  if (!darfSchreiben) {
    if (feld === 'titel') return <h2 className="auf-titel-lesen">{wert}</h2>;
    return wert ? <p className="auf-beschreibung-lesen">{wert}</p> : <p className="leise">Keine Beschreibung.</p>;
  }

  async function speichern(mitVersion: boolean) {
    const neu = feld === 'titel' ? entwurf.trim() : entwurf;
    if (neu === start.current.wert) {
      setAktiv(false);
      return setKonflikt('');
    }
    if (feld === 'titel' && !neu) {
      setEntwurf(start.current.wert);
      setAktiv(false);
      return;
    }
    speichert.current = true;
    setAktiv(false);
    try {
      const a = await api.patch<Aufgabe>(url, { [feld]: neu, ...(mitVersion ? { version: start.current.version } : {}) });
      setKonflikt('');
      setFehler('');
      start.current = { wert: a[feld], version: a.version };
      geaendert(a);
    } catch (f) {
      if (f instanceof ApiFehler && f.status === 409) setKonflikt(f.message);
      else setFehler(f instanceof Error ? f.message : 'Fehler');
    } finally {
      speichert.current = false;
    }
  }

  const fremdGeaendert = aktiv && wert !== start.current.wert;
  const gemeinsam = {
    value: entwurf,
    'aria-label': bezeichnung,
    onFocus: () => {
      if (!aktiv && !konflikt) start.current = { wert, version };
      setAktiv(true);
    },
    onBlur: () => void speichern(true),
  };

  return (
    <div className="auf-text-bearbeiten">
      {mehrzeilig ? (
        <Textfeld
          {...gemeinsam}
          rows={Math.min(14, Math.max(3, entwurf.split('\n').length + 1))}
          placeholder="Was ist zu tun? Hintergründe, Absprachen …"
          onChange={(e) => setEntwurf(e.target.value)}
        />
      ) : (
        <Eingabe
          {...gemeinsam}
          className="auf-titel-eingabe"
          maxLength={300}
          onChange={(e) => setEntwurf(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') {
              setEntwurf(start.current.wert);
              setTimeout(() => (e.target as HTMLInputElement).blur());
            }
          }}
        />
      )}
      {fremdGeaendert && <Meldung art="info">Jemand anderes hat dieses Feld gerade geändert. Beim Speichern wirst du gefragt.</Meldung>}
      {konflikt && (
        <Meldung art="fehler">
          {konflikt}
          <div className="reihe auf-konflikt-knoepfe">
            <Knopf klein onClick={() => void speichern(false)}>
              Meine Fassung trotzdem speichern
            </Knopf>
            <Knopf
              klein
              art="leise"
              onClick={() => {
                setKonflikt('');
                setEntwurf(wert);
                start.current = { wert, version };
              }}
            >
              Neue Fassung übernehmen
            </Knopf>
          </div>
          {feld === 'beschreibung' && (
            <details className="auf-fremde-fassung">
              <summary>Aktuelle Fassung ansehen</summary>
              <p>{wert || '(leer)'}</p>
            </details>
          )}
        </Meldung>
      )}
      <Meldung>{fehler}</Meldung>
    </div>
  );
}

function DatumFeld({ wert, speichern }: { wert: string | null; speichern(d: string | null): void }) {
  const [d, setD] = useState(wert ?? '');
  useEffect(() => setD(wert ?? ''), [wert]);
  const fertig = (v: string) => {
    if (v === (wert ?? '')) return;
    if (!v) return speichern(null);
    const jahr = Number(v.slice(0, 4));
    if (jahr >= 2000 && jahr <= 2100) speichern(v);
  };
  return (
    <div className="reihe auf-datum">
      <Eingabe
        type="date"
        value={d}
        onChange={(e) => {
          setD(e.target.value);
          // Erst speichern, wenn das Jahr vollständig ist (beim Tippen kommt zuerst „0002-…“)
          fertig(e.target.value);
        }}
        onBlur={(e) => fertig(e.target.value)}
        aria-label="Fälligkeitsdatum"
      />
      {wert && (
        <Knopf art="leise" klein onClick={() => speichern(null)} aria-label="Datum entfernen" title="Datum entfernen">
          ✕
        </Knopf>
      )}
    </div>
  );
}

function Checkliste({
  a,
  url,
  darfSchreiben,
  geaendert,
}: {
  a: Aufgabe;
  url(rest?: string): string;
  darfSchreiben: boolean;
  geaendert(a: Aufgabe): void;
}) {
  const [neu, setNeu] = useState('');
  const aktion = useAktion();
  const fertig = a.punkte.filter((p) => p.erledigt).length;
  async function los<T extends Aufgabe>(fn: () => Promise<T>) {
    const r = await aktion.ausfuehren(fn);
    if (r) geaendert(r);
    return r;
  }
  if (!darfSchreiben && a.punkte.length === 0) return null;
  return (
    <section className="auf-abschnitt">
      <h3>
        Checkliste {a.punkte.length > 0 && <span className="leise klein">{fertig} von {a.punkte.length} erledigt</span>}
      </h3>
      {a.punkte.length > 0 && (
        <div className="auf-fortschritt" aria-hidden>
          <span style={{ width: `${(fertig / a.punkte.length) * 100}%` }} />
        </div>
      )}
      <ul className="auf-punkte">
        {a.punkte.map((p) => (
          <li key={p.id} className={p.erledigt ? 'auf-punkt-fertig' : ''}>
            <label className="schalter">
              <input
                type="checkbox"
                checked={p.erledigt}
                disabled={!darfSchreiben || aktion.laeuft}
                onChange={(e) => void los(() => api.patch<Aufgabe>(url(`/punkte/${p.id}`), { erledigt: e.target.checked }))}
              />
              <span>{p.text}</span>
            </label>
            {darfSchreiben && (
              <button
                type="button"
                className="knopf knopf-leise knopf-klein"
                aria-label={`Punkt „${p.text}“ entfernen`}
                onClick={() => void los(() => api.delete<Aufgabe>(url(`/punkte/${p.id}`)))}
              >
                ✕
              </button>
            )}
          </li>
        ))}
      </ul>
      {darfSchreiben && (
        <form
          className="auf-punkt-neu"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!neu.trim()) return;
            if (await los(() => api.post<Aufgabe>(url('/punkte'), { text: neu }))) setNeu('');
          }}
        >
          <Eingabe placeholder="Punkt hinzufügen …" value={neu} onChange={(e) => setNeu(e.target.value)} maxLength={500} aria-label="Neuer Punkt" />
          <Knopf type="submit" klein disabled={!neu.trim() || aktion.laeuft}>
            {Symbol.plus}
          </Knopf>
        </form>
      )}
      <Meldung>{aktion.fehler}</Meldung>
    </section>
  );
}

function Kommentare({
  a,
  url,
  ichId,
  darfSchreiben,
  darfFremde,
}: {
  a: Aufgabe;
  url(rest?: string): string;
  ichId: string;
  darfSchreiben: boolean;
  darfFremde: boolean;
}) {
  const { daten, setDaten, fehler } = useDaten(() => api.get<AufgabeDetail>(url()).then((d) => d.kommentare), [a.id]);
  const [text, setText] = useState('');
  const [bearbeite, setBearbeite] = useState<{ id: string; text: string } | null>(null);
  const aktion = useAktion();

  useEffect(() => {
    const h = (e: Event) => {
      const n = (e as CustomEvent<AufgabenLive>).detail;
      if (n.typ === 'kommentare' && n.aufgabeId === a.id) setDaten(n.kommentare);
    };
    window.addEventListener('auf:kommentare', h);
    return () => window.removeEventListener('auf:kommentare', h);
  }, [a.id, setDaten]);

  async function los(fn: () => Promise<Kommentar[]>) {
    const r = await aktion.ausfuehren(fn);
    if (r) setDaten(r);
    return r;
  }

  return (
    <section className="auf-abschnitt">
      <h3>Kommentare {daten && daten.length > 0 && <span className="leise klein">{daten.length}</span>}</h3>
      <Meldung>{fehler || aktion.fehler}</Meldung>
      {daten?.length === 0 && <p className="leise">Noch keine Kommentare.</p>}
      <ul className="auf-kommentare">
        {daten?.map((c) => (
          <li key={c.id} className={c.autor?.id === ichId ? 'auf-eigener' : ''}>
            <div className="auf-kommentar-kopf">
              <strong>{c.autor?.name ?? 'Unbekannt'}</strong>
              <span className="leise klein">
                {datumZeit(c.erstelltAm)}
                {c.geaendertAm ? ' · bearbeitet' : ''}
              </span>
              <span className="abstand" />
              {darfSchreiben && c.autor?.id === ichId && bearbeite?.id !== c.id && (
                <Knopf art="leise" klein onClick={() => setBearbeite({ id: c.id, text: c.text })}>
                  Bearbeiten
                </Knopf>
              )}
              {darfSchreiben && (c.autor?.id === ichId || darfFremde) && (
                <Knopf
                  art="leise"
                  klein
                  aria-label="Kommentar löschen"
                  onClick={() => confirm('Kommentar löschen?') && void los(() => api.delete<Kommentar[]>(url(`/kommentare/${c.id}`)))}
                >
                  {Symbol.muell}
                </Knopf>
              )}
            </div>
            {bearbeite?.id === c.id ? (
              <form
                className="stapel"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (await los(() => api.patch<Kommentar[]>(url(`/kommentare/${c.id}`), { text: bearbeite.text }))) setBearbeite(null);
                }}
              >
                <Textfeld value={bearbeite.text} onChange={(e) => setBearbeite({ id: c.id, text: e.target.value })} rows={3} aria-label="Kommentar bearbeiten" />
                <div className="reihe">
                  <Knopf type="submit" art="haupt" klein disabled={!bearbeite.text.trim() || aktion.laeuft}>
                    Speichern
                  </Knopf>
                  <Knopf klein art="leise" onClick={() => setBearbeite(null)}>
                    Abbrechen
                  </Knopf>
                </div>
              </form>
            ) : (
              <p className="auf-kommentar-text">{c.text}</p>
            )}
          </li>
        ))}
      </ul>
      {darfSchreiben && (
        <form
          className="stapel auf-kommentar-neu"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!text.trim()) return;
            if (await los(() => api.post<Kommentar[]>(url('/kommentare'), { text }))) setText('');
          }}
        >
          <Textfeld
            placeholder="Kommentar schreiben …"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            aria-label="Neuer Kommentar"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) (e.currentTarget.form as HTMLFormElement | null)?.requestSubmit();
            }}
          />
          <div className="reihe">
            <Knopf type="submit" klein art="haupt" disabled={!text.trim() || aktion.laeuft}>
              Kommentieren
            </Knopf>
            <span className="leise klein">Strg + Enter</span>
          </div>
        </form>
      )}
    </section>
  );
}

function AbgebenDialog({
  offen,
  schliessen,
  kandidaten,
  abgeben,
}: {
  offen: boolean;
  schliessen(): void;
  kandidaten: PersonKurz[];
  abgeben(an: string | null): Promise<void>;
}) {
  const [an, setAn] = useState('');
  return (
    <Dialog
      titel="Aufgabe abgeben"
      offen={offen}
      schliessen={schliessen}
      fuss={
        <>
          <Knopf art="haupt" onClick={() => void abgeben(an || null)}>
            {an ? 'Weitergeben' : 'Abgeben'}
          </Knopf>
          <Knopf art="leise" onClick={schliessen}>
            Abbrechen
          </Knopf>
        </>
      }
    >
      <Feld titel="An wen?" hinweis="Ohne Auswahl wirst du nur aus der Aufgabe entfernt.">
        <Auswahl value={an} onChange={(e) => setAn(e.target.value)}>
          <option value="">– niemanden, nur abgeben –</option>
          {kandidaten.map((k) => (
            <option key={k.id} value={k.id}>
              {k.name}
            </option>
          ))}
        </Auswahl>
      </Feld>
    </Dialog>
  );
}
